import React from 'react';
import DialtoneIcon from '../assets/svg/products/dialtone-icon.svg';
import SecureframeIcon from '../assets/svg/products/secureframe-icon.svg';
import ViziblyIcon from '../assets/svg/products/vizibly-icon.png';
import ChuttAppIcon from '../assets/svg/products/chuttapp.png';
import ZoneBuiltIcon from '../assets/svg/products/zonebuilt-icon.svg';
import ConstraqIcon from '../assets/svg/products/constraq-icon.svg';
import HausketchIcon from '../assets/svg/products/hausketch-icon.svg';
import BildaIcon from '../assets/svg/products/bilda-icon.svg';

const pitchDeckLink = 'https://www.canva.com/design/DAG5Q000-dk/6xO9QuVxTIjvCyimr5pjnw/view?utm_content=DAG5Q000-dk&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=ha428c1ae37';

const experience = [
    {
        name: 'Dialtone',
        role: 'Founding Engineer',
        logo: DialtoneIcon,
        description: 'I help build AI-powered customer support experiences from the ground up. I work across the entire stack, partnering closely with founders to rapidly ship customer-facing features, build scalable integrations, and continuously improve the AI agent experience.',
        highlights: [
            'Built and shipped a production AI chat agent.',
            'Designed and implemented end-to-end third-party integrations.',
            'Improved AI response latency and overall user experience.',
            'Worked across frontend, backend services, APIs, and system architecture.',
            'Collaborated directly with founders to iterate quickly based on customer feedback.',
        ],
    },
    {
        name: 'Secureframe',
        role: 'Senior Software Engineer',
        logo: SecureframeIcon,
        description: 'I built compliance automation software used by thousands of businesses worldwide. My work focused on reliable frontend experiences, stronger platform performance, and features that helped customers automate complex compliance workflows.',
        highlights: [
            'Led development across core compliance workflows and dashboards.',
            'Built features used daily by thousands of customers.',
            'Improved application performance and overall user experience.',
            'Collaborated closely with product, design, and backend engineering teams.',
            'Conducted code reviews and mentored engineers on frontend best practices.',
        ],
    },
    {
        name: 'Vizibly',
        role: 'Software Engineer',
        logo: ViziblyIcon,
        description: 'I worked on production web applications in a fast-moving startup environment. My focus was improving performance, resolving critical user-facing issues, and shipping polished customer experiences with engineering and design.',
        highlights: [
            'Resolved critical production issues.',
            'Built customer-facing product features.',
            'Optimized high-traffic pages for better performance.',
            'Improved overall user experience through close collaboration with designers.',
        ],
    },
];

const building = [
    {
        name: 'ZoneBuilt Technologies',
        role: 'Founder & Software Engineer',
        logo: ZoneBuiltIcon,
        description: 'ZoneBuilt is a construction technology company I am building to modernize how construction projects are managed across Africa. It brings together products for project management, architectural marketplaces, and professional discovery.',
        highlights: [
            'Construction technology ecosystem.',
            'Built for African project teams and property owners.',
            'Connects planning, collaboration, discovery, and delivery.',
        ],
    },
    {
        name: 'ConstraQ',
        logo: ConstraqIcon,
        description: 'A modern construction project management platform built for property owners, contractors, consultants, and project teams.',
        highlights: [
            'Project management',
            'Construction timelines',
            'Team collaboration',
            'Discussion boards and messaging',
            'Document management',
            'Financial tracking',
            'Billing and subscriptions',
            'Project updates',
            'Multi-company support',
        ],
    },
    {
        name: 'Hausketch',
        logo: HausketchIcon,
        description: 'A marketplace for premium architectural plans where clients can discover house designs, purchase plans, request customizations, and collaborate directly with architects and designers.',
        highlights: [
            'Building plan marketplace',
            'Architect studios',
            'Secure purchasing',
            'Custom design requests',
            'Digital and physical plan delivery',
            'Architect verification',
            'Reviews and ratings',
        ],
    },
    {
        name: 'Bilda',
        logo: BildaIcon,
        description: 'A platform for discovering trusted construction professionals including architects, engineers, contractors, artisans, and specialists.',
        highlights: [
            'Verified professional profiles',
            'Smart search and discovery',
            'Portfolio showcase',
            'Reviews and ratings',
            'Business profiles',
            'Construction-focused networking',
        ],
    },
    {
    name: 'ChuttApp',
    logo: ChuttAppIcon,
    role: 'Founder & Software Engineer',
    description:
        'ChuttApp is a super app that brings together messaging, digital payments, commerce, and an open mini-app ecosystem into a single platform. The goal is to eliminate the need for multiple apps by enabling people and businesses to communicate, transact, shop, and build services within one unified experience.',
    highlights: [
        'Designed the product vision, architecture, and user experience from the ground up.',
        'Building an integrated ecosystem combining chat, wallets, QR payments, peer-to-peer transfers, and commerce.',
        'Created an open mini-app platform that enables businesses and developers to launch services inside ChuttApp.',
        'Focused on creating infrastructure that scales across multiple African markets.',
        'Leading product strategy, software architecture, and full-stack development.',
    ],
    cta: {
        label: 'View Pitch Deck',
        url: pitchDeckLink,
    },
},
];

function Experience() {
    return (
        <div className="portfolio-sections">
            <PortfolioSection
                title="Experience"
                items={experience}
            />
            <PortfolioSection
                title="Things I'm Building"
                items={building}
            />
        </div>
    );
}

const PortfolioSection = ({ title, items }) => (
    <section className="portfolio-section">
        <div className="portfolio-section__head">
            <h3>{title}</h3>
        </div>
        <div className="portfolio-grid">
            {items.map((item, index) => (
                <PortfolioCard key={item.name} item={item} index={index} />
            ))}
        </div>
    </section>
);

const PortfolioCard = ({ item, index }) => {
    const hasHighlights = item.highlights && item.highlights.length > 0;

    return (
        <article className="portfolio-card" style={{ animationDelay: `${index * 80}ms` }}>
            <div className="portfolio-card__top">
                <Logo item={item} />
                <div>
                    <h4>{item.name}</h4>
                    {item.role ? <p className="portfolio-card__role">{item.role}</p> : null}
                </div>
            </div>
            <p className="portfolio-card__description">{item.description}</p>
            {hasHighlights ? (
                <ul className="portfolio-card__highlights">
                    {item.highlights.map((highlight) => (
                        <li key={highlight}>{highlight}</li>
                    ))}
                </ul>
            ) : null}
            {item.cta ? (
                <a className="portfolio-card__button" href={item.cta.url} target="_blank" rel="noopener noreferrer">
                    {item.cta.label}
                </a>
            ) : null}
        </article>
    );
};

const Logo = ({ item }) => {
    if (item.logo) {
        return (
            <div className="portfolio-card__logo portfolio-card__logo--image">
                <img src={item.logo} alt={`${item.name} logo`} />
            </div>
        );
    }

    return (
        <div className="portfolio-card__logo" aria-hidden="true">
            {item.name.slice(0, 1)}
        </div>
    );
};

export default Experience;
